import { PROMPT_SECTIONS } from './types';
import type { PromptSectionKey } from './types';

/**
 * Parser for prompts Boston already authored by hand.
 *
 * 15 of 23 Boston agents already carry a full five-section prompt inside the
 * free-text `instructions` field, because the form gave them nowhere else to
 * put it. Those prompts are the authors' work and must be adopted verbatim,
 * never rewritten or wrapped in competing platform scaffolding.
 *
 * The field is TipTap HTML with NO newlines — headings live inside <p> tags
 * ("<p># Personality</p><p>You are Sam, ...</p>"). So structure has to be
 * recovered from the markup before any markdown parsing, and list/line breaks
 * preserved through it.
 */

const BLOCK_TAGS = 'p|div|section|article|header|footer|h[1-6]|ul|ol|table|tr|blockquote|pre';

const ENTITIES: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&rsquo;': '’',
  '&lsquo;': '‘',
  '&ldquo;': '“',
  '&rdquo;': '”',
  '&mdash;': '—',
  '&ndash;': '–',
};

function decodeEntities(text: string): string {
  let out = text;
  for (const [entity, char] of Object.entries(ENTITIES)) {
    out = out.split(entity).join(char);
  }
  return out.replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)));
}

/**
 * HTML -> plain text, preserving block structure.
 *
 * Unlike the legacy `stripHtml` (which collapses all whitespace to single
 * spaces and destroys every line break), this keeps paragraphs, list items and
 * headings on their own lines so markdown headings remain parseable.
 */
export function htmlToText(html: string | null | undefined): string {
  let text = String(html ?? '');
  if (!text) return '';

  // Nothing to do for content that is already plain text.
  const hasMarkup = /<[a-z!/][^>]*>/i.test(text);
  if (hasMarkup) {
    text = text
      .replace(/<\s*br\s*\/?\s*>/gi, '\n')
      .replace(/<\s*li[^>]*>/gi, '\n- ')
      .replace(new RegExp(`<\\s*/\\s*(${BLOCK_TAGS})\\s*>`, 'gi'), '\n')
      .replace(new RegExp(`<\\s*(${BLOCK_TAGS})[^>]*>`, 'gi'), '\n')
      .replace(/<[^>]+>/g, '');
  }

  return decodeEntities(text)
    .split('\n')
    .map((line) => line.replace(/[ \t ]+/g, ' ').trimEnd())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Aliases authors actually use for each section heading. */
const SECTION_ALIASES: Record<PromptSectionKey, string[]> = {
  personality: ['personality', 'persona', 'character', 'who you are'],
  environment: ['environment', 'setting', 'context', 'situation', 'scenario'],
  tone: ['tone', 'tone & style', 'tone and style', 'voice', 'communication style'],
  goal: ['goal', 'goals', 'objective', 'objectives', 'task', 'your goal'],
  guardrails: ['guardrails', 'guardrail', 'boundaries', 'rules', 'constraints', 'safety'],
};

/**
 * Match a line that is *only* a section heading.
 * Accepts: "# Personality", "## Personality", "**Personality**", "PERSONALITY", "Personality:".
 * Rejects prose that merely mentions the word, so "Your personality should..." is not a heading.
 */
function headingKey(line: string): PromptSectionKey | null {
  const cleaned = line
    .trim()
    .replace(/^#{1,6}\s*/, '')
    .replace(/^\*\*|\*\*$/g, '')
    .replace(/^_+|_+$/g, '')
    .replace(/[:\-–—]\s*$/, '')
    .trim()
    .toLowerCase();

  if (!cleaned || cleaned.length > 24) return null;

  const looksLikeHeading = /^#{1,6}\s/.test(line.trim()) || /^\*\*.*\*\*$/.test(line.trim()) || /:$/.test(line.trim()) || line.trim() === line.trim().toUpperCase();
  if (!looksLikeHeading) return null;

  for (const key of PROMPT_SECTIONS) {
    if (SECTION_ALIASES[key].includes(cleaned)) return key;
  }
  return null;
}

export interface ParsedPrompt {
  /** Authored content per section, verbatim (markdown/lists/line breaks preserved). */
  sections: Partial<Record<PromptSectionKey, string>>;
  /** Text before the first recognised heading. */
  preamble: string;
  /** Which sections the author actually supplied. */
  matched: PromptSectionKey[];
  /** True when the author clearly wrote a structured prompt (>=3 sections). */
  isAuthored: boolean;
  /** Sections that appear more than once — a conversion error, not a merge. */
  duplicates: PromptSectionKey[];
}

/**
 * Parse authored five-section content out of a free-text/HTML field.
 * Content is adopted verbatim — this never rewrites the author's words.
 */
export function parseAuthoredSections(raw: string | null | undefined): ParsedPrompt {
  const text = htmlToText(raw);
  const result: ParsedPrompt = { sections: {}, preamble: '', matched: [], isAuthored: false, duplicates: [] };
  if (!text) return result;

  const lines = text.split('\n');
  const buckets = new Map<PromptSectionKey, string[]>();
  const seen: PromptSectionKey[] = [];
  let current: PromptSectionKey | null = null;
  const preamble: string[] = [];

  for (const line of lines) {
    const key = headingKey(line);
    if (key) {
      if (seen.includes(key) && !result.duplicates.includes(key)) result.duplicates.push(key);
      seen.push(key);
      current = key;
      if (!buckets.has(key)) buckets.set(key, []);
      continue;
    }
    if (current) buckets.get(current)!.push(line);
    else preamble.push(line);
  }

  for (const [key, body] of buckets) {
    const content = body.join('\n').replace(/\n{3,}/g, '\n\n').trim();
    if (content) {
      result.sections[key] = content;
      result.matched.push(key);
    }
  }

  result.preamble = preamble.join('\n').trim();
  result.isAuthored = result.matched.length >= 3;
  return result;
}

/**
 * Best-effort persona identity detection, for MIGRATION ONLY.
 *
 * Runtime must use the first-class `persona_name` column — deriving identity
 * from prose on every session is too fragile. Every name this proposes has to
 * appear in the conversion report for a human to confirm.
 */
const NON_NAMES = new Set([
  'not', 'the', 'a', 'an', 'now', 'never', 'always', 'here', 'there', 'about',
  'currently', 'speaking', 'talking', 'playing', 'acting', 'roleplaying', 'in', 'at', 'on',
]);

export function extractPersonaName(raw: string | null | undefined): string | null {
  const text = htmlToText(raw);
  if (!text) return null;

  // "You are Sam, an experienced..." / "You are Dr Sarah Chen, a cardiologist"
  // A title alone ("Dr") is not a name, so titles must be followed by a word.
  const match = text.match(
    /\byou\s+are\s+((?:(?:Dr|Prof|Mr|Mrs|Ms)\.?\s+)?[A-Z][\w'’\-]+(?:\s+[A-Z][\w'’\-]+){0,2})\s*(?=[,.]|\s+(?:an?|the|who|is|—|-))/,
  );
  if (!match) return null;

  const name = match[1].trim().replace(/\s+/g, ' ');
  if (!name || name.length > 40) return null;

  // Reject shouted words and sentence starts like "You are NOT permitted…".
  const words = name.split(' ');
  const meaningful = words.filter((w) => !/^(Dr|Prof|Mr|Mrs|Ms)\.?$/.test(w));
  if (meaningful.length === 0) return null;
  if (meaningful.every((w) => w === w.toUpperCase() && w.length > 1)) return null;
  if (NON_NAMES.has(meaningful[0].toLowerCase())) return null;

  return name;
}
